// src/seo/schema.mjs
// schema.org JSON-LD üreticileri. Saf fonksiyonlardır: hem React (istemci) hem de
// scripts/postbuild.mjs (statik ön-oluşturma) tarafından kullanılır.

export const abs = (site, path = '/') => (path.startsWith('http') ? path : `${site.url}${path === '/' ? '/' : path}`);

export const ids = (site) => ({
  org: `${site.url}/#organization`,
  website: `${site.url}/#website`,
});

export function organization(site) {
  const o = site.organization;
  return {
    '@type': 'Organization',
    '@id': ids(site).org,
    name: site.name,
    url: abs(site, '/'),
    logo: { '@type': 'ImageObject', url: abs(site, site.logo), width: 512, height: 512 },
    parentOrganization: { '@type': 'Organization', name: o.name, legalName: o.legalName, url: o.url },
    contactPoint: [{ '@type': 'ContactPoint', contactType: 'customer support', telephone: o.telephone, url: abs(site, '/oyuntu/iletisim'), availableLanguage: ['tr', 'en'] }],
    address: o.addresses.map((a) => ({ '@type': 'PostalAddress', ...a })),
    sameAs: o.sameAs,
  };
}

export function website(site) {
  return {
    '@type': 'WebSite',
    '@id': ids(site).website,
    name: site.name,
    alternateName: 'Oyuntu Kart Oyunları',
    url: abs(site, '/'),
    description: site.description,
    inLanguage: 'tr-TR',
    publisher: { '@id': ids(site).org },
  };
}

export function breadcrumb(site, items) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      ...(it.path ? { item: abs(site, it.path) } : {}),
    })),
  };
}

export function faqPage(site, faq, path) {
  if (!faq || !faq.length) return null;
  return {
    '@type': 'FAQPage',
    ...(path ? { '@id': `${abs(site, path)}#faq` } : {}),
    mainEntity: faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export function gameItemList(site, games) {
  return {
    '@type': 'ItemList',
    name: 'Oyuntu kart oyunları',
    numberOfItems: games.length,
    itemListElement: games.map((g, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: abs(site, `/oyun/${g.id}`),
      name: g.name,
      image: abs(site, `/img/screens/${g.id}-landscape.webp`),
    })),
  };
}

/** Oyun sayfası: VideoGame + WebApplication niteliklerini taşıyan tek düğüm. */
export function videoGame(site, game, content) {
  const url = abs(site, `/oyun/${game.id}`);
  const c = content || {};
  const node = {
    '@type': ['VideoGame', 'WebApplication'],
    '@id': `${url}#game`,
    name: game.name,
    alternateName: `${game.name} Oyna`,
    url,
    description: c.intro || game.short,
    image: abs(site, `/img/screens/${game.id}-landscape.webp`),
    inLanguage: 'tr-TR',
    genre: ['Kart oyunu', 'İskambil'],
    gamePlatform: ['Web tarayıcı', 'Android', 'iOS', 'Windows', 'macOS'],
    applicationCategory: 'GameApplication',
    applicationSubCategory: 'CardGame',
    operatingSystem: 'Tüm işletim sistemleri (tarayıcı)',
    browserRequirements: 'HTML5 ve JavaScript destekli güncel bir tarayıcı',
    isAccessibleForFree: true,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'TRY', availability: 'https://schema.org/InStock' },
    playMode: 'SinglePlayer',
    author: { '@id': ids(site).org },
    publisher: { '@id': ids(site).org },
    isPartOf: { '@id': ids(site).website },
  };
  if (c.minPlayers || c.maxPlayers) {
    node.numberOfPlayers = { '@type': 'QuantitativeValue', minValue: c.minPlayers || 1, maxValue: c.maxPlayers || c.minPlayers || 1 };
  }
  if (c.keywords) node.keywords = c.keywords.join(', ');
  if (c.updated) node.dateModified = c.updated;
  return node;
}

/** Rehber sayfası: Article + HowTo adımları. */
export function guideArticle(site, game, content) {
  const url = abs(site, `/rehber/${game.id}`);
  const g = content.guide || {};
  const article = {
    '@type': 'Article',
    '@id': `${url}#article`,
    headline: g.title || `${game.name} Nasıl Oynanır?`,
    description: g.description || content.metaDescription,
    url,
    mainEntityOfPage: url,
    image: abs(site, `/img/og/${game.id}.jpg`),
    inLanguage: 'tr-TR',
    author: { '@id': ids(site).org },
    publisher: { '@id': ids(site).org },
    about: { '@id': `${abs(site, `/oyun/${game.id}`)}#game` },
    articleSection: 'Oyun Rehberi',
  };
  if (content.updated) {
    article.dateModified = content.updated;
    article.datePublished = content.published || content.updated;
  }
  const nodes = [article];
  if (content.howToPlay && content.howToPlay.length) {
    nodes.push({
      '@type': 'HowTo',
      '@id': `${url}#howto`,
      name: `${game.name} nasıl oynanır?`,
      description: content.intro,
      inLanguage: 'tr-TR',
      tool: content.deck ? [{ '@type': 'HowToTool', name: content.deck }] : undefined,
      totalTime: undefined,
      step: content.howToPlay.map((text, i) => ({ '@type': 'HowToStep', position: i + 1, name: `Adım ${i + 1}`, text, url: `${url}#adim-${i + 1}` })),
    });
  }
  return nodes;
}

export function graph(...nodes) {
  return { '@context': 'https://schema.org', '@graph': nodes.flat().filter(Boolean) };
}
